import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Badge, ErrorBox } from '../components/common';
import { formatDate } from '../labels';

// Vérification d'identité par NIP (facultative) auprès du service Identité & NIP : jamais un rattachement au
// registre ni une délivrance. Les données vérifiées ne complètent la demande qu'après preuve de contrôle du
// demandeur (connexion par NIP, ou pièce d'identité contrôlée au guichet).

type CitizenStatus =
  | 'VERIFIED'
  | 'DIVERGENCE'
  | 'NOT_FOUND'
  | 'MISMATCH'
  | 'PENDING'
  | 'REVOKED'
  | 'UNAVAILABLE'
  | 'MANUAL_REVIEW';

/** Seuls les attributs accordés à CASIJUD sont renvoyés : chacun peut manquer. */
interface NipAttributes {
  surname?: string;
  givenNames?: string;
  birthYear?: number;
  birthMonth?: number;
  birthDay?: number;
  birthPlace?: string;
}

interface NipResult {
  status: CitizenStatus;
  message: string;
  attributes: NipAttributes | null;
  provenance: string | null;
  checkedAt: string | null;
  canConfirm: boolean;
}

interface NipSummary {
  enabled: boolean;
  connectedWithNip: boolean;
  verified: boolean;
  confirmedAt: string | null;
  attemptsLeft: number;
  last: NipResult | null;
  agent?: {
    status: string | null;
    method: 'verify_nip' | 'connexion_nip' | null;
    proofOfControl: 'connexion_nip' | 'piece_controlee_guichet' | 'aucune' | null;
    proofRef: string | null;
    verifiedReference: boolean;
    identityStatus: 'DECLAREE' | 'VERIFIEE' | 'LITIGIEUSE' | null;
    motif: string | null;
    checkedAt: string | null;
    divergences: string[];
    attempts: number;
    reviewReason: string | null;
    reviewedAt: string | null;
    reviewedBy: string | null;
    reviewNote: string | null;
  };
}

const AGENT_STATUS: Record<string, { label: string; tone: string }> = {
  VERIFIED: { label: 'Vérifiée', tone: 'ok' },
  NOT_FOUND: { label: 'Introuvable', tone: 'warn' },
  MISMATCH: { label: 'Identité non concordante', tone: 'warn' },
  PENDING: { label: 'En cours de confirmation', tone: 'warn' },
  REVOKED: { label: 'NIP révoqué', tone: 'warn' },
  UNAVAILABLE: { label: 'Service indisponible', tone: 'muted' },
  MANUAL_REVIEW: { label: 'Examen humain demandé', tone: 'warn' },
};

export const REVIEW_REASON: Record<string, string> = {
  introuvable: 'NIP introuvable',
  ambigu: 'Réponse ambiguë',
  indisponible: 'Service indisponible',
  revue_manuelle: 'Examen humain demandé par le service',
  divergence: 'Saisie différente des données vérifiées',
  non_concordant: 'Identité non concordante avec le NIP',
  revoque: 'NIP révoqué',
  en_attente: 'Identité en cours de confirmation',
  litige: 'Identité signalée en litige',
  correction: 'Identité corrigée par le service',
};

const PROOF: Record<string, string> = {
  connexion_nip: 'Connexion par NIP (mot de passe et code)',
  piece_controlee_guichet: 'Pièce d’identité contrôlée au guichet',
  aucune: 'Aucune (NIP saisi en ligne)',
};

const IDENTITY_STATUS: Record<string, string> = {
  DECLAREE: 'Identité déclarée',
  VERIFIEE: 'Identité vérifiée',
  LITIGIEUSE: 'Identité litigieuse',
};

const DOCUMENT_TYPES = [
  'Carte nationale d’identité',
  'Passeport',
  'Carte de séjour',
  'Autre pièce officielle avec photographie',
];

const FIELD_LABEL: Record<string, string> = {
  surname: 'Nom',
  givenNames: 'Prénoms',
  birthYear: 'Année de naissance',
  birthMonth: 'Mois de naissance',
  birthDay: 'Jour de naissance',
  birthPlace: 'Lieu de naissance',
};

function birth(a: NipAttributes) {
  const date = [a.birthDay, a.birthMonth, a.birthYear]
    .map((v) => (v === undefined ? null : String(v).padStart(2, '0')))
    .filter(Boolean)
    .join('/');
  return [date, a.birthPlace].filter(Boolean).join(', ') || 'non communiquée';
}

/** Carte de la demande : vérification par l'usager (brouillon) ou lecture par l'agent d'instruction. */
export function NipCard({
  applicationId,
  status,
  role,
  autoOpen,
  onChange,
}: {
  applicationId: string;
  status: string;
  role: string;
  autoOpen?: boolean;
  onChange: () => void;
}) {
  const [summary, setSummary] = useState<NipSummary | null>(null);
  const load = useCallback(
    () =>
      api<NipSummary>(`/demandes/${applicationId}/nip`)
        .then(setSummary)
        .catch(() => setSummary(null)),
    [applicationId],
  );
  useEffect(() => {
    void load();
  }, [load]);
  if (!summary?.enabled) return null;
  if (summary.agent) return <AgentView id={applicationId} s={summary} onDone={setSummary} />;
  if (role !== 'owner' && role !== 'counter') return null;
  if (status !== 'brouillon') {
    return summary.confirmedAt ? (
      <section className="card flow">
        <h2>Vérification NIP</h2>
        <p className="alert alert-ok">Identité vérifiée par NIP le {formatDate(summary.confirmedAt)}.</p>
      </section>
    ) : null;
  }
  return (
    <CitizenView
      id={applicationId}
      s={summary}
      counter={role === 'counter'}
      autoOpen={autoOpen}
      onDone={() => {
        void load();
        onChange();
      }}
    />
  );
}

function CitizenView({
  id,
  s,
  counter,
  autoOpen,
  onDone,
}: {
  id: string;
  s: NipSummary;
  counter: boolean;
  autoOpen?: boolean;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(!!autoOpen);
  const [declined, setDeclined] = useState(false);
  const [nip, setNip] = useState('');
  const [consent, setConsent] = useState(false);
  const [documentChecked, setDocumentChecked] = useState(false);
  const [documentType, setDocumentType] = useState(DOCUMENT_TYPES[0]!);
  const [result, setResult] = useState<NipResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  if (s.confirmedAt)
    return (
      <section className="card flow">
        <h2>Vérification NIP</h2>
        <p className="alert alert-ok">
          Identité vérifiée par NIP et confirmée le {formatDate(s.confirmedAt)}. Elle a complété votre
          identité déclarée.
        </p>
      </section>
    );

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  };
  const verify = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      setResult(
        await api<NipResult>(`/demandes/${id}/nip/verifier`, {
          method: 'POST',
          body: {
            nip,
            idempotencyKey: crypto.randomUUID(),
            consent,
            ...(counter && documentChecked
              ? { identityDocumentChecked: true, identityDocumentType: documentType }
              : {}),
          },
        }),
      );
      setNip('');
    });
  };
  /** Identité de la connexion par NIP : le service a exigé mot de passe et code, c'est la preuve de contrôle. */
  const useConnected = () =>
    run(async () => {
      setResult(
        await api<NipResult>(`/demandes/${id}/nip/identite-connectee`, {
          method: 'POST',
          body: { idempotencyKey: crypto.randomUUID(), consent: true },
        }),
      );
      setOpen(true);
    });
  const confirm = () =>
    run(async () => {
      await api(`/demandes/${id}/nip/confirmer`, { method: 'POST' });
      onDone();
    });
  const shown = result ?? s.last;

  return (
    <section className="card flow nip-card" aria-labelledby="nip-h">
      <h2 id="nip-h">Vérifier mon identité avec mon NIP</h2>
      {declined ? (
        <div className="ch">
          <span className="muted">Vous continuez sans NIP : un agent vérifiera votre identité.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeclined(false)}>
            Utiliser mon NIP
          </button>
        </div>
      ) : !open && !shown ? (
        <>
          <p className="small muted" style={{ margin: 0 }}>
            Facultatif. Votre numéro d’identification personnel peut confirmer votre identité et compléter
            votre saisie. Sans NIP, votre demande suit exactement le même circuit.
          </p>
          {s.connectedWithNip && (
            <p className="small" style={{ margin: 0 }}>
              Vous êtes connecté avec votre NIP : vos données d’identité vérifiées peuvent compléter la
              demande, après votre confirmation. Aucune information judiciaire n’est transmise au service
              d’identification.
            </p>
          )}
          <ErrorBox error={error} />
          <div className="actions">
            {s.connectedWithNip && s.attemptsLeft > 0 && (
              <button type="button" className="btn" disabled={busy} onClick={() => void useConnected()}>
                Utiliser mon identité vérifiée
              </button>
            )}
            <button
              type="button"
              className={s.connectedWithNip ? 'btn btn-ghost' : 'btn'}
              onClick={() => setOpen(true)}
            >
              Vérifier mon identité avec mon NIP
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setDeclined(true)}>
              Continuer sans NIP
            </button>
          </div>
        </>
      ) : (
        <>
          {shown && (
            <div className={`alert ${shown.status === 'VERIFIED' ? 'alert-ok' : 'alert-info'}`} role="status">
              {shown.message}
            </div>
          )}
          {result?.attributes && result.canConfirm && (
            <div className="card tint flow nip-result">
              <dl className="details">
                <dt>Nom</dt>
                <dd>{result.attributes.surname ?? 'non communiqué'}</dd>
                <dt>Prénoms</dt>
                <dd>{result.attributes.givenNames ?? 'non communiqués'}</dd>
                <dt>Naissance</dt>
                <dd>{birth(result.attributes)}</dd>
                <dt>Provenance</dt>
                <dd className="small">
                  {result.provenance}
                  {result.checkedAt ? `, ${formatDate(result.checkedAt)}` : ''}
                </dd>
              </dl>
              <div className="actions">
                <button type="button" className="btn" disabled={busy} onClick={() => void confirm()}>
                  Confirmer ces informations
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setResult(null)}>
                  Ce ne sont pas mes informations
                </button>
              </div>
            </div>
          )}
          {s.attemptsLeft > 0 && !(result?.canConfirm && result.attributes) && (
            <form className="nip-form" onSubmit={verify}>
              <p className="small muted" style={{ margin: 0 }}>
                Votre NIP sert uniquement à vérifier votre identité pour cette demande. Il est transmis de
                façon sécurisée au service d’identification avec votre nom, vos prénoms et votre date de
                naissance, jamais avec une information judiciaire ; il n’est ni conservé ni affiché, et seul
                le résultat de la vérification est enregistré. Saisissez d’abord votre nom, vos prénoms et
                votre année de naissance dans la demande.
              </p>
              <div className="fg">
                <div className="field">
                  <label htmlFor="nip">NIP</label>
                  <input
                    id="nip"
                    name="nip"
                    autoComplete="off"
                    inputMode="text"
                    required
                    maxLength={64}
                    value={nip}
                    onChange={(e) => setNip(e.target.value)}
                  />
                  <span className="hint">
                    {s.attemptsLeft} essai{s.attemptsLeft > 1 ? 's' : ''} restant
                    {s.attemptsLeft > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <label className="checkbox">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>J’ai pris connaissance de l’usage de mon NIP décrit ci-dessus.</span>
              </label>
              {counter && (
                <div className="flow">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={documentChecked}
                      onChange={(e) => setDocumentChecked(e.target.checked)}
                    />
                    <span>J’ai contrôlé la pièce d’identité présentée par la personne.</span>
                  </label>
                  {documentChecked && (
                    <div className="field">
                      <label htmlFor="nip-doc">Pièce contrôlée</label>
                      <select
                        id="nip-doc"
                        value={documentType}
                        onChange={(e) => setDocumentType(e.target.value)}
                      >
                        {DOCUMENT_TYPES.map((d) => (
                          <option key={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <span className="hint">
                    Sans contrôle de la pièce, les données vérifiées ne sont ni montrées ni reprises.
                  </span>
                </div>
              )}
              <ErrorBox error={error} />
              <div className="actions">
                <button className="btn" type="submit" disabled={busy || !consent || !nip.trim()}>
                  {busy ? 'Vérification…' : 'Vérifier'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setOpen(false);
                    setResult(null);
                    setDeclined(true);
                  }}
                >
                  Continuer sans NIP
                </button>
              </div>
            </form>
          )}
          {result?.canConfirm && <ErrorBox error={error} />}
        </>
      )}
    </section>
  );
}

function AgentView({ id, s, onDone }: { id: string; s: NipSummary; onDone: (s: NipSummary) => void }) {
  const a = s.agent!;
  const [note, setNote] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  if (!a.status) return null;
  const st =
    a.status === 'VERIFIED' && a.divergences.length
      ? { label: 'Saisie différente', tone: 'warn' }
      : (AGENT_STATUS[a.status] ?? { label: a.status, tone: 'muted' });
  const review = async () => {
    setError(null);
    try {
      onDone(await api<NipSummary>(`/demandes/${id}/nip/revue`, { method: 'POST', body: { note } }));
    } catch (e) {
      setError(e as ApiError);
    }
  };
  return (
    <section className="card flow" aria-labelledby="nip-a-h">
      <h2 id="nip-a-h">Vérification NIP</h2>
      <dl className="details">
        <dt>Résultat</dt>
        <dd>
          <Badge tone={st.tone}>{st.label}</Badge>
          {s.confirmedAt && (
            <span className="small muted">
              {a.proofOfControl === 'piece_controlee_guichet'
                ? ' - préremplie au guichet (pièce contrôlée)'
                : ' - confirmée par l’usager (connexion par NIP)'}
            </span>
          )}
        </dd>
        <dt>Source</dt>
        <dd>
          Service Identité & NIP{a.checkedAt ? `, ${formatDate(a.checkedAt)}` : ''}
          {a.method === 'connexion_nip' && (
            <div className="small muted">Identité de la connexion par NIP</div>
          )}
        </dd>
        <dt>Preuve de contrôle</dt>
        <dd>
          {a.proofOfControl ? PROOF[a.proofOfControl] : '-'}
          {a.proofRef ? ` : ${a.proofRef}` : ''}
        </dd>
        {a.identityStatus && (
          <>
            <dt>Au service</dt>
            <dd>{IDENTITY_STATUS[a.identityStatus]}</dd>
          </>
        )}
        {a.motif && (
          <>
            <dt>Motif</dt>
            <dd>{a.motif}</dd>
          </>
        )}
        <dt>Référence vérifiée</dt>
        <dd>
          {a.verifiedReference ? 'Enregistrée (identifiant propre à CASIJUD, jamais le NIP)' : 'Aucune'}
        </dd>
        {a.divergences.length > 0 && (
          <>
            <dt>Différences</dt>
            <dd>{a.divergences.map((f) => FIELD_LABEL[f] ?? f).join(', ')}</dd>
          </>
        )}
        <dt>Essais</dt>
        <dd>{a.attempts}</dd>
      </dl>
      <p className="small muted" style={{ margin: 0 }}>
        Indication d’identification seulement : elle ne rattache pas la demande au registre et n’autorise
        aucune délivrance.
      </p>
      {a.reviewReason &&
        (a.reviewedAt ? (
          <p className="alert alert-ok">
            Revue faite par {a.reviewedBy} le {formatDate(a.reviewedAt)} : {a.reviewNote}
          </p>
        ) : (
          <div className="card tint flow">
            <p style={{ margin: 0 }}>
              <strong>Identité à revoir</strong> - {REVIEW_REASON[a.reviewReason] ?? a.reviewReason}. Vérifiez
              l’identité sur les pièces jointes, sans conclure à une fraude. La préparation du bulletin reste
              bloquée jusqu’à cette revue.
            </p>
            <div className="field">
              <label htmlFor="nip-note">Comment l’identité a été vérifiée</label>
              <input id="nip-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <ErrorBox error={error} />
            <div className="actions">
              <button
                type="button"
                className="btn"
                disabled={note.trim().length < 5}
                onClick={() => void review()}
              >
                Identité vérifiée manuellement
              </button>
            </div>
          </div>
        ))}
    </section>
  );
}

/** File des demandes dont l'identité est à revoir (espace Délivrance). */
export function NipReviewQueue() {
  const [rows, setRows] = useState<
    {
      id: string;
      reference: string;
      status: string;
      submitted_at: string | null;
      nip_review_reason: string;
    }[]
  >([]);
  useEffect(() => {
    void api<typeof rows>('/verification-nip/revue')
      .then(setRows)
      .catch(() => setRows([]));
  }, []);
  if (!rows.length) return null;
  return (
    <section className="card np" aria-labelledby="nip-q-h">
      <div className="pl-card-h">
        <h2 id="nip-q-h">Identité à revoir (NIP)</h2>
      </div>
      <div className="table-wrap">
        <table aria-labelledby="nip-q-h">
          <thead>
            <tr>
              <th scope="col">Demande</th>
              <th scope="col">Motif</th>
              <th scope="col">Déposée</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <Link to={`/espace/demandes/${r.id}`} className="mono">
                    {r.reference}
                  </Link>
                </td>
                <td data-l="Motif">{REVIEW_REASON[r.nip_review_reason] ?? r.nip_review_reason}</td>
                <td data-l="Déposée">{r.submitted_at ? formatDate(r.submitted_at) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Rapprochement d'une fiche du registre avec une identité vérifiée (dossiers historiques Numera / Oria). */
interface LinkState {
  enabled: boolean;
  reference: { status: 'valide' | 'suspendu'; suspended: boolean; updatedAt: string } | null;
  pendingProposalId: string | null;
  history: {
    id: string;
    action: string;
    service_status: string | null;
    source: string;
    source_ref: string | null;
    fromMigration: boolean;
    evidence: string;
    reason: string | null;
    created_at: string;
    agent: string;
  }[];
}

const LINK_ACTION: Record<string, string> = {
  tentative: 'Interrogation',
  proposition: 'Proposition',
  validation: 'Validation',
  rejet: 'Rejet',
  suspension: 'Suspension',
};

const SOURCES: Record<string, string> = {
  numera: 'Dossier Numera',
  oria: 'Extraction Oria',
  registre: 'Fiche du registre',
  demande: 'Demande de bulletin',
  notification: 'Notification du service',
};

export function IdentityLinkCard({ personId, canDecide }: { personId: string; canDecide: boolean }) {
  const [state, setState] = useState<LinkState | null>(null);
  const [method, setMethod] = useState<'verify_nip' | 'match'>('match');
  const [nip, setNip] = useState('');
  const [source, setSource] = useState('numera');
  const [sourceRef, setSourceRef] = useState('');
  const [evidence, setEvidence] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(
    () =>
      api<LinkState>(`/registre/personnes/${personId}/identite-nip`)
        .then(setState)
        .catch(() => setState(null)),
    [personId],
  );
  useEffect(() => {
    void load();
  }, [load]);
  if (!state?.enabled) return null;

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e as ApiError);
    } finally {
      setBusy(false);
    }
  };
  const attempt = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const r = await api<{ message: string }>(`/registre/personnes/${personId}/identite-nip`, {
        method: 'POST',
        body: {
          method,
          ...(method === 'verify_nip' ? { nip } : {}),
          source,
          ...(sourceRef ? { sourceRef } : {}),
          evidence,
        },
      });
      setMessage(r.message);
      setNip('');
      await load();
    });
  };
  const decide = (decision: 'valider' | 'rejeter') =>
    run(async () => {
      setState(
        await api<LinkState>(`/registre/rapprochements-nip/${state.pendingProposalId}/decision`, {
          method: 'POST',
          body: { decision, reason },
        }),
      );
      setReason('');
      setMessage(null);
    });

  return (
    <section className="card flow" aria-labelledby="idl-h">
      <h2 id="idl-h">Identité vérifiée (service Identité & NIP)</h2>
      <p className="small muted" style={{ margin: 0 }}>
        Rapprochement prudent : au mieux une proposition, validée par un autre agent après revérification.
        Aucune fusion, aucune inscription ; seule l’identité de la fiche est transmise, jamais son contenu
        judiciaire.
      </p>
      {state.reference ? (
        <p className={`alert ${state.reference.status === 'valide' ? 'alert-ok' : 'alert-info'}`}>
          {state.reference.status === 'valide'
            ? `Référence d’identité vérifiée enregistrée (${formatDate(state.reference.updatedAt)}).`
            : 'Référence suspendue à la suite d’une notification du service : nouvelle validation nécessaire.'}
        </p>
      ) : null}
      {message && (
        <p className="alert alert-info" role="status">
          {message}
        </p>
      )}
      {state.pendingProposalId && canDecide && (
        <div className="card tint flow">
          <p style={{ margin: 0 }}>
            <strong>Proposition en attente.</strong> Contrôlez les pièces du dossier ; vous ne pouvez pas
            valider une proposition que vous avez faite.
          </p>
          <div className="field">
            <label htmlFor="idl-reason">Motif de la décision</label>
            <input id="idl-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="actions">
            <button
              type="button"
              className="btn"
              disabled={busy || reason.trim().length < 5}
              onClick={() => void decide('valider')}
            >
              Valider le rapprochement
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy || reason.trim().length < 5}
              onClick={() => void decide('rejeter')}
            >
              Rejeter
            </button>
          </div>
        </div>
      )}
      {state.reference?.status !== 'valide' && !state.pendingProposalId && (
        <form className="flow" onSubmit={attempt}>
          <div className="fg">
            <div className="field">
              <label htmlFor="idl-method">Interroger avec</label>
              <select
                id="idl-method"
                value={method}
                onChange={(e) => setMethod(e.target.value as 'verify_nip' | 'match')}
              >
                <option value="match">L’identité de la fiche (date complète)</option>
                <option value="verify_nip">Un NIP présenté</option>
              </select>
            </div>
            {method === 'verify_nip' && (
              <div className="field">
                <label htmlFor="idl-nip">NIP</label>
                <input
                  id="idl-nip"
                  autoComplete="off"
                  maxLength={64}
                  value={nip}
                  onChange={(e) => setNip(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="field">
              <label htmlFor="idl-source">Dossier d’origine</label>
              <select id="idl-source" value={source} onChange={(e) => setSource(e.target.value)}>
                {['numera', 'oria', 'registre', 'demande'].map((k) => (
                  <option key={k} value={k}>
                    {SOURCES[k]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="idl-ref">Référence du dossier</label>
              <input id="idl-ref" value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="idl-evidence">Preuve du rapprochement (pièces comparées)</label>
            <input
              id="idl-evidence"
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              required
            />
          </div>
          <ErrorBox error={error} />
          <div className="actions">
            <button
              className="btn"
              type="submit"
              disabled={busy || evidence.trim().length < 5 || (method === 'verify_nip' && !nip.trim())}
            >
              {busy ? 'Interrogation…' : 'Interroger le service'}
            </button>
          </div>
        </form>
      )}
      {state.pendingProposalId && !canDecide && <ErrorBox error={error} />}
      {state.history.length > 0 && (
        <div className="table-wrap">
          <table aria-labelledby="idl-h">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Étape</th>
                <th scope="col">Agent</th>
                <th scope="col">Source et preuve</th>
              </tr>
            </thead>
            <tbody>
              {state.history.map((h) => (
                <tr key={h.id}>
                  <td data-l="Date">{formatDate(h.created_at)}</td>
                  <td data-l="Étape">
                    {LINK_ACTION[h.action] ?? h.action}
                    {h.service_status && (
                      <div className="small muted">
                        {AGENT_STATUS[h.service_status]?.label ?? h.service_status}
                      </div>
                    )}
                  </td>
                  <td data-l="Agent">{h.agent}</td>
                  <td data-l="Source et preuve" className="small">
                    {h.fromMigration ? 'Migration des données' : (SOURCES[h.source] ?? h.source)}
                    {h.source_ref ? ` - ${h.source_ref}` : ''} : {h.evidence}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
